import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
} from '@mui/material';
import { apiService } from '../services/api';
import { CertificateVerifyResult } from '../types';

const CertificateVerify: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<CertificateVerifyResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!token?.trim()) {
        setLoading(false);
        setError('Invalid verification link.');
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const result = await apiService.getCertificateVerify(token.trim());
        setData(result);
      } catch (e) {
        console.error(e);
        setError('Unable to verify this certificate at the moment.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Box sx={{ mt: 4 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Verifying certificate...</Typography>
        </Box>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">{error || 'Certificate not found.'}</Alert>
      </Container>
    );
  }

  if (!data.valid) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Certificate not found
          </Typography>
          <Typography sx={{ mb: 3 }}>
            No certificate could be found for this verification link. It may be invalid or not yet issued.
          </Typography>
          <Button variant="contained" component={RouterLink} to="/">
            Go to Kursu Timor
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4,
        }}
      >
        {/* Left: Certificate preview */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            Course Certificate
          </Typography>
          <Typography variant="h4" sx={{ mb: 2 }}>
            {data.course_name}
          </Typography>
          <Box
            sx={{
              backgroundColor: 'transparent',
              borderRadius: 0,
              border: 'none',
              boxShadow: 'none',
              overflow: 'hidden',
              width: '100%',
              maxWidth: 'min(1122px, 100%)',
              maxHeight: 'calc(100vh - 14rem)',
              aspectRatio: '1122 / 794',
              mb: 2,
            }}
          >
            {data.image_url ? (
              <Box
                component="img"
                src={data.image_url}
                alt="Certificate preview"
                sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : data.html ? (
              <iframe
                title="Certificate preview"
                srcDoc={data.html}
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                }}
              >
                Preview unavailable
              </Box>
            )}
          </Box>
          <Button variant="contained" component={RouterLink} to="/">
            Go to Kursu Timor
          </Button>
        </Box>

        {/* Right: Info */}
        <Box
          sx={{
            flex: { xs: 'none', md: '0 0 340px' },
            minWidth: { xs: '100%', md: 320 },
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Information
            </Typography>
            <Typography variant="body1">
              <strong>Complete Name:</strong> {data.recipient_name}
            </Typography>
            <Typography variant="body1">
              <strong>Email:</strong> {data.email || '—'}
            </Typography>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Description
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              This verifies that <strong>{data.recipient_name}</strong> has completed{' '}
              <strong>{data.course_name}</strong> in Kursu Timor at {data.date}.
            </Typography>
          </Box>
          <Typography variant="body2">
            <strong>Serial:</strong> {data.serial}
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              disabled={downloading || !token}
              onClick={async () => {
                if (!token) return;
                try {
                  setDownloading(true);
                  await apiService.downloadCertificateByToken(
                    token,
                    `certificate_${data.serial || 'kursu_timor'}.pdf`
                  );
                } catch (e) {
                  console.error(e);
                  // In a real app you might show a toast here
                  setError('Unable to download certificate right now.');
                } finally {
                  setDownloading(false);
                }
              }}
            >
              {downloading ? 'Downloading…' : 'Download Certificate (PDF)'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default CertificateVerify;

